// src/pages/Admin/Articles/AddArticleModal.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import { createArticle, getArticles } from "../../../services/articlesServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";

const defaultForm = (ordre = 0) => ({
  nom: "",
  description: "",
  prix: "",
  taille: "",
  quantite_stock: "",
  categorie: "",
  newCategorie: "",
  photo_url: "",
  is_active: true,
  is_stockable: true,
  ordre_article: ordre,
});

export default function AddArticleModal({ isOpen, onClose, refreshList }) {
  const [formData, setFormData] = useState(defaultForm());
  const [categories, setCategories] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);

  const loadMeta = useCallback(async () => {
    setLoadingMeta(true);
    try {
      const articles = await getArticles();
      const maxOrder = Math.max(...articles.map((a) => a.ordre_article || 0), 0);
      const unique = [
        ...new Set(
          articles.map((a) => (a.categorie || "").trim()).filter((c) => c !== "")
        ),
      ].sort((a, b) => a.localeCompare(b, "fr"));

      setCategories(unique);
      setFormData(defaultForm(maxOrder + 1));
    } catch (e) {
      console.error("Erreur meta (ordre/catégories)", e);
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setFormData(defaultForm());
    loadMeta();
  }, [isOpen, loadMeta]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleNewCatKeyDown = (e) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const canSubmit = useMemo(() => {
    const catFinal =
      formData.categorie === "_new"
        ? (formData.newCategorie || "").trim()
        : (formData.categorie || "").trim();

    return (
      !loadingMeta &&
      formData.nom.trim() &&
      String(formData.prix).trim() !== "" &&
      catFinal
    );
  }, [formData, loadingMeta]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalCategorie =
      formData.categorie === "_new"
        ? (formData.newCategorie || "").trim()
        : (formData.categorie || "").trim();

    if (!finalCategorie) {
      toast.error("Veuillez saisir ou choisir une catégorie");
      return;
    }

    const payload = {
      ...formData,
      categorie: finalCategorie,
      prix: formData.prix === "" ? null : Number(formData.prix),
      quantite_stock:
        formData.is_stockable && formData.quantite_stock !== ""
          ? Number(formData.quantite_stock)
          : 0,
      ordre_article:
        formData.ordre_article === "" ? 0 : Number(formData.ordre_article),
    };
    delete payload.newCategorie;

    try {
      await createArticle(payload);
      toast.success("Article ajouté avec succès");
      refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'article", error);
      toast.error("Erreur lors de l'ajout de l'article");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      {/* Modal (structure flex pour body scrollable mobile) */}
      <div className="relative w-full max-w-2xl flex flex-col overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70 max-h-[92dvh]">
        {/* Header */}
        <div className="shrink-0 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Ajouter un article
          </h3>
        </div>

        {/* Body scrollable */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-y-auto p-5 sm:p-6 space-y-5"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {/* Nom + Description */}
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Nom
              </label>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                required
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                placeholder="Ex : T-shirt club"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Description
              </label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Description courte…"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
          </div>

          {/* Prix / Taille */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Prix (€)
              </label>
              <input
                type="number"
                step="0.01"
                name="prix"
                value={formData.prix}
                onChange={handleChange}
                required
                placeholder="Ex : 12.50"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Taille (si vêtement/kimonos)
              </label>
              <input
                type="text"
                name="taille"
                value={formData.taille}
                onChange={handleChange}
                placeholder="S, M, L…"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
          </div>

          {/* Stockable + quantité */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <input
                id="is_stockable"
                type="checkbox"
                name="is_stockable"
                checked={formData.is_stockable}
                onChange={handleChange}
                className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-400"
              />
              <label
                htmlFor="is_stockable"
                className="text-sm font-medium text-slate-800 dark:text-slate-100"
              >
                Gestion d’un stock
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Quantité en stock
              </label>
              <input
                type="number"
                name="quantite_stock"
                value={formData.quantite_stock}
                onChange={handleChange}
                placeholder="Ex : 25"
                disabled={!formData.is_stockable}
                className={`w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400 ${
                  formData.is_stockable
                    ? "border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                    : "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                }`}
              />
            </div>
          </div>

          {/* Catégorie */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Catégorie
            </label>
            <select
              name="categorie"
              value={formData.categorie}
              onChange={handleChange}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
            >
              <option value="">— Choisir une catégorie —</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
              <option value="_new">+ Créer une nouvelle catégorie</option>
            </select>

            {formData.categorie === "_new" && (
              <div className="mt-2">
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Nouvelle catégorie
                </label>
                <input
                  type="text"
                  value={formData.newCategorie}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, newCategorie: e.target.value }))
                  }
                  onKeyDown={handleNewCatKeyDown}
                  placeholder="Ex : Boutique / Prêt / Divers…"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                  Astuce : appuyer sur Entrée ne soumettra pas la modale.
                </p>
              </div>
            )}
          </div>

          {/* Photo URL + aperçu + Boutique + Ordre */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Lien URL de la photo (en ligne)
              </label>
              <input
                type="text"
                name="photo_url"
                value={formData.photo_url}
                onChange={handleChange}
                placeholder="https://…"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                Vous pourrez <strong>modifier</strong> ou <strong>téléverser</strong> une image
                depuis votre ordinateur <strong>après la création</strong>, en éditant l’article.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Aperçu
              </label>
              <div className="h-[84px] rounded-xl border border-dashed border-slate-300 dark:border-slate-700 flex items-center justify-center overflow-hidden">
                {formData.photo_url ? (
                  <img
                    src={formData.photo_url}
                    alt="aperçu"
                    className="h-full object-contain"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : (
                  <span className="text-xs text-slate-500">Aucun aperçu</span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <input
                id="is_active"
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-400"
              />
              <label
                htmlFor="is_active"
                className="text-sm font-medium text-slate-800 dark:text-slate-100"
              >
                Afficher sur la boutique
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Ordre (sera utilisé pour le tri)
              </label>
              <input
                type="number"
                name="ordre_article"
                value={formData.ordre_article}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
          </div>

          {/* Spacer pour éviter que le contenu passe sous le footer sur mobile */}
          <div className="h-2" />
        </form>

        {/* Footer (hors zone scroll) */}
        <div className="shrink-0 px-5 sm:px-6 py-3 border-t border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95">
          <div className="flex justify-end gap-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text="Enregistrer"
              icon={FaSave}
              type="submit"
              form="" // on cible le form le plus proche (comportement par défaut)
              disabled={!canSubmit}
              className={`!rounded-xl gap-2 ${!canSubmit ? "opacity-60 pointer-events-none" : ""}`}
              onClick={(e) => {
                // déclenche la soumission du <form> parent
                const form = e.currentTarget.closest("div.relative")?.querySelector("form");
                form?.requestSubmit?.();
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
